import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cartApi } from '@/services/cartService';
import { queryKeys } from './queryKeys';
import { useSnackbarStore } from '@/stores/snackbarStore';
import { useAuthStore } from '@/stores/authStore';

/**
 * Hook to fetch cart items
 * Only fetches when user is logged in to prevent 401 errors
 */
export const useCart = () => {
  const { isLoggedIn } = useAuthStore();

  return useQuery({
    queryKey: queryKeys.cart.items(),
    queryFn: cartApi.getCart,
    enabled: isLoggedIn, // Only fetch when user is logged in
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

/**
 * Hook to add item to cart
 */
export const useAddToCart = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarStore();

  return useMutation({
    mutationFn: (trackId: string) => cartApi.addItem(trackId),
    onSuccess: (data) => {
      // Invalidate cart queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.cart.all });

      // Invalidate tracks queries to update isInCart status across all pages
      queryClient.invalidateQueries({ queryKey: queryKeys.tracks.all });

      if (data.success) {
        showSnackbar('Added to cart successfully');
      } else {
        showSnackbar(data.message || 'Failed to add to cart');
      }
    },
    onError: (error: Error & { code?: string; error?: { code?: string } }) => {
      const errorCode = (error as any)?.code || (error as any)?.error?.code;

      // 팝업으로 처리되는 에러는 스낵바 표시하지 않음
      if (errorCode === 'SOLD_OUT' || errorCode === 'TRACK_NOT_ON_SALE') {
        return;
      }

      showSnackbar(error.message || 'Failed to add to cart');
    },
  });
};

/**
 * Hook to remove item from cart
 */
export const useRemoveFromCart = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarStore();

  return useMutation({
    mutationFn: (cartItemId: string) => cartApi.removeItem(cartItemId),
    onSuccess: () => {
      // Invalidate cart queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.cart.all });

      // Invalidate tracks queries to update isInCart status across all pages
      queryClient.invalidateQueries({ queryKey: queryKeys.tracks.all });

      showSnackbar('Removed from cart successfully');
    },
    onError: (error: Error) => {
      showSnackbar(error.message || 'Failed to remove from cart');
    },
  });
};
